import { ArrowDownIcon, ArrowUpIcon, CaretSortIcon, EyeNoneIcon } from '@radix-ui/react-icons'
import { Column } from '@tanstack/react-table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@components/common/dropdown-menu'
import { Button } from '@components/common/button'
import { cn } from '@pma/utils/helpers'

import useTranslator from '@hooks/use-translator'
import { useLocation } from 'react-router-dom'
import React, { useEffect } from 'react'
import { GroupedTasksContext } from '@pma/components/projects/grouped-tasks/context/GroupedTasksProvider'

interface DataTableColumnHeaderProps<TData, TValue> extends React.HTMLAttributes<HTMLDivElement> {
  column: Column<TData, TValue>
  title: string
}

export function DataTableColumnHeader<TData, TValue>({
  column,
  title,
  className,
}: DataTableColumnHeaderProps<TData, TValue>) {
  const { translate, locale } = useTranslator()
  const isHebrew = locale === 'he'
  const location = useLocation()
  const localStorageConst = 'task-table-order'
  const [showIcon, setShowIcon] = React.useState<any>({ order: "false", column: "" })
  const { setPageSorting, pageSorting, setPagination, paginationData } =
    React.useContext(GroupedTasksContext)
  const projectId = location.pathname.split("projects/")[1]
  const loggedInUser = localStorage.getItem("rog_user")
    ? JSON.parse(localStorage?.getItem("rog_user") || "")
    : ""
  if (!column.getCanSort()) {
    return (
      <span
        className={cn(
          "flex items-center min-w-[2ch] text-black w-full",
          className,
          isHebrew ? "rtl text-right" : "ltr text-left",
        )}
      >
        {translate(title)}
      </span>
    )
  }

  useEffect(() => {
    if (pageSorting.sortBy === column.id) {
      if (pageSorting.orderBy === "asc") {
        column.toggleSorting(false)
        setShowIcon({ order: "asc", column: column.id })
      } else if (pageSorting.orderBy === "desc") {
        column.toggleSorting(true)
        setShowIcon({ order: "desc", column: column.id })
      } else {
        setShowIcon({ order: "false", column: column.id })
      }
    } else {
      setShowIcon({ order: "false", column: "" })
    }
  }, [pageSorting])

  const handleAscending = () => {
    column.toggleSorting(false)
    setListingOrder("asc")
    const payload = {
      orderBy: "asc",
      sortBy: column.id,
    }
    setShowIcon({ order: "asc", column: column.id })
    setPageSorting(payload)
    setPagination({ pageIndex: 1, pageSize: paginationData.pageSize })
  }

  const handleDescending = () => {
    column.toggleSorting(true)
    setListingOrder("desc")
    const payload = {
      orderBy: "desc",
      sortBy: column.id,
    }
    setShowIcon({ order: "desc", column: column.id })
    setPageSorting(payload)
    setPagination({ pageIndex: 1, pageSize: paginationData.pageSize })
  }

  const handleVisibility = () => {
    column.toggleVisibility(false)
  }

  const setListingOrder = (status: string) => {
    const orderArr = localStorage?.getItem(localStorageConst)

    if (orderArr) {
      const arr = JSON.parse(orderArr)

      if (arr.length === 0) {
        arr.push({ projectId, order_key: column.id, order_value: status, user: loggedInUser.email })
        localStorage.setItem(localStorageConst, JSON.stringify(arr))
      } else if (arr.length > 0) {
        const found = arr.find((val: any) => val.projectId === projectId)

        if (found) {
          const index = arr.indexOf(found)
          arr[index]['order_key'] = column.id
          arr[index]['order_value'] = status

          localStorage.setItem(localStorageConst, JSON.stringify(arr))
        } else {
          const obj = { projectId, order_key: column.id, order_value: status, user: loggedInUser.email }
          const newArr = [...arr, obj]

          localStorage.setItem(localStorageConst, JSON.stringify(newArr))
        }
      }
    }
  }

  const RenderIcon = () => {
    if (showIcon.column === column.id) {
      if (showIcon.order === 'asc') {
        return <ArrowUpIcon className='ml-2 h-4 w-4' />
      } else if (showIcon.order === 'desc') {
        return <ArrowDownIcon className='ml-2 h-4 w-4' />
      } else if (showIcon.order === 'false') {
        return <CaretSortIcon className='ml-2 h-4 w-4' />
      }
    } else return <CaretSortIcon className='ml-2 h-4 w-4' />
  }

  return (
    <div className={cn('flex items-center space-x-2 min-w-0 w-full', className)}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant='ghost'
            size='sm'
            className='-ml-3 h-8 text-black data-[state=open]:bg-accent truncate'
          >
            <span className='min-w-0 flex-1 text-left truncate capitalize'>{translate(title)}</span>
            <RenderIcon />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='start'>
          <DropdownMenuItem onClick={handleAscending}>
            <ArrowUpIcon className='mr-2 h-3.5 w-3.5 text-muted-foreground/70' />
            {title === 'Priority' ? 'High priority' : 'Asc'}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleDescending}>
            <ArrowDownIcon className='mr-2 h-3.5 w-3.5 text-muted-foreground/70' />
            {title === 'Priority' ? 'Low priority' : 'Desc'}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleVisibility}>
            <EyeNoneIcon className='mr-2 h-3.5 w-3.5 text-muted-foreground/70' />
            Hide
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
